import React from "react";
import { Link } from "react-router-dom";
import SAT_HDS from "../../config/satHds";

/**
 * SAT SELF DISCOVERY — homepage section
 * Kein "Test"-Look. Zwei Premium Experience Cards mit Kreis- und Radar-Signale.
 * Öffnet die Landingpages für Circle of Life™ und Survival of the Fittest™.
 */
const CircleGlyph = () => (
  <svg viewBox="0 0 200 200" className="w-40 h-40 sm:w-48 sm:h-48" aria-hidden="true">
    <defs>
      <radialGradient id="circleGlow" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#F6E7B8" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#F6E7B8" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="100" cy="100" r="90" fill="url(#circleGlow)" />
    {[...Array(12)].map((_, i) => {
      const angle = (i * 30 * Math.PI) / 180;
      const x1 = 100 + Math.cos(angle) * 35;
      const y1 = 100 + Math.sin(angle) * 35;
      const x2 = 100 + Math.cos(angle) * (70 + Math.sin(i * 1.7) * 8);
      const y2 = 100 + Math.sin(angle) * (70 + Math.sin(i * 1.7) * 8);
      return (
        <g key={i}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D4AF37" strokeWidth="1.2" opacity="0.55" />
          <circle cx={x2} cy={y2} r="2.8" fill="#D4AF37" opacity="0.85" />
        </g>
      );
    })}
    <circle cx="100" cy="100" r="88" fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.35" />
    <circle cx="100" cy="100" r="60" fill="none" stroke="#D4AF37" strokeWidth="0.4" opacity="0.25" strokeDasharray="2 4" />
    <circle cx="100" cy="100" r="6" fill="#F6E7B8" />
  </svg>
);

const RadarGlyph = () => (
  <svg viewBox="0 0 200 200" className="w-40 h-40 sm:w-48 sm:h-48" aria-hidden="true">
    <defs>
      <radialGradient id="radarGlow" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#7C6FE0" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#7C6FE0" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="100" cy="100" r="90" fill="url(#radarGlow)" />
    {[30, 55, 80].map((r) => (
      <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="#D4AF37" strokeWidth="0.5" opacity="0.35" />
    ))}
    {[...Array(9)].map((_, i) => {
      const angle = (i * 40 * Math.PI) / 180 - Math.PI / 2;
      const x = 100 + Math.cos(angle) * 80;
      const y = 100 + Math.sin(angle) * 80;
      return <line key={i} x1="100" y1="100" x2={x} y2={y} stroke="#D4AF37" strokeWidth="0.5" opacity="0.3" />;
    })}
    {/* profile polygon */}
    <polygon
      points={[...Array(9)]
        .map((_, i) => {
          const r = 30 + ((i * 37) % 45);
          const a = (i * 40 * Math.PI) / 180 - Math.PI / 2;
          return `${(100 + Math.cos(a) * r).toFixed(1)},${(100 + Math.sin(a) * r).toFixed(1)}`;
        })
        .join(" ")}
      fill="#D4AF37"
      fillOpacity="0.18"
      stroke="#D4AF37"
      strokeWidth="1"
    />
    <circle cx="100" cy="100" r="4" fill="#F6E7B8" />
  </svg>
);

const ExperienceCard = ({ to, kicker, title, tagline, description, cta, glyph, tone, testid }) => {
  const toneClasses =
    tone === "circle"
      ? "from-[#0E1330] via-[#161C40] to-[#0E1330] border-[#D4AF37]/25"
      : "from-[#101635] via-[#1A1F45] to-[#0B0F26] border-[#D4AF37]/25";

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative block overflow-hidden rounded-2xl border bg-gradient-to-br ${toneClasses} p-8 sm:p-10 transition-all duration-500 hover:border-[#D4AF37]/60 hover:shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]`}
    >
      {/* subtle grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 0%, transparent 45%), radial-gradient(circle at 80% 70%, #7C6FE0 0%, transparent 40%)" }} />

      <div className="relative flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37]/80 uppercase mb-2">{kicker}</div>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight">{title}</h3>
            <p className="mt-2 text-white/70 italic text-base">{tagline}</p>
          </div>
          <div className="opacity-90 transition-transform duration-700 group-hover:scale-105 group-hover:rotate-3">
            {glyph}
          </div>
        </div>

        <p className="text-white/75 text-sm sm:text-base leading-relaxed max-w-md">{description}</p>

        <div className="mt-2 flex items-center gap-3 text-[#D4AF37]">
          <span className="text-xs tracking-[0.25em] uppercase">{cta}</span>
          <span className="inline-block w-8 h-px bg-[#D4AF37] transition-all duration-500 group-hover:w-16" />
        </div>
      </div>
    </Link>
  );
};

const SelfDiscoverySection = () => {
  const s = SAT_HDS.section;
  return (
    <section
      id="self-discovery"
      data-testid="self-discovery-section"
      className="relative overflow-hidden bg-[#08102A] py-20 sm:py-28"
    >
      {/* Backdrop */}
      <div className="pointer-events-none absolute inset-0 opacity-40"
           style={{ background: "radial-gradient(1200px 500px at 50% 0%, rgba(212,175,55,0.10), transparent 70%), radial-gradient(900px 400px at 20% 100%, rgba(124,111,224,0.12), transparent 70%)" }} />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <div className="text-[11px] tracking-[0.4em] text-[#D4AF37] uppercase mb-4">
            {s.kicker}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F6E7B8] leading-[1.05]">
            {s.headline}
          </h2>
          <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
            {s.sub}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <ExperienceCard
            to={SAT_HDS.paths.circle}
            testid="card-circle-of-life"
            kicker="SAT EXPERIENCE · 01"
            title={SAT_HDS.experiences.circle.trademark}
            tagline={SAT_HDS.experiences.circle.tagline}
            description={SAT_HDS.experiences.circle.publicDescription}
            cta={SAT_HDS.experiences.circle.cta}
            glyph={<CircleGlyph />}
            tone="circle"
          />
          <ExperienceCard
            to={SAT_HDS.paths.survival}
            testid="card-survival-of-the-fittest"
            kicker="SAT EXPERIENCE · 02"
            title={SAT_HDS.experiences.survival.trademark}
            tagline={SAT_HDS.experiences.survival.tagline}
            description={SAT_HDS.experiences.survival.publicDescription}
            cta={SAT_HDS.experiences.survival.cta}
            glyph={<RadarGlyph />}
            tone="survival"
          />
        </div>

        {/* Bridge to Life Map */}
        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-xl border border-[#D4AF37]/20 bg-white/[0.02] p-6">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase whitespace-nowrap">
            SAT LIFE MAP™
          </div>
          <div className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/50 to-transparent hidden sm:block" />
          <p className="text-white/70 text-sm leading-relaxed">
            Nach beiden Experiences entsteht automatisch dein persönliches Gesamtbild —
            die Landkarte deiner Entwicklung.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SelfDiscoverySection;
